import {hashKey, QueryKey} from "@tanstack/query-core";
import {createContext, PropsWithChildren, useCallback, useContext, useEffect, useMemo, useState} from "react";
import {useQuery, useQueryClient, UseQueryOptions} from "@tanstack/react-query";
import {InfallibleUseQueryResult, useInfallibleQuery} from "./useInfallibleQuery.tsx";

export interface RefreshSet {
    register: (qk: QueryKey) => void;
    unregister: (qk: QueryKey) => void;
    invalidate: () => Promise<unknown>;
}

export function RefreshProvider({children}: PropsWithChildren) {
    const [qkRefCountMap] = useState<Map<string, [QueryKey, number]>>(new Map<string, [QueryKey, number]>());
    const tqc = useQueryClient();

    const register = useCallback((qk: QueryKey) => {
        const sk = hashKey(qk);
        const xrec = qkRefCountMap.get(sk);
        if (xrec) {
            xrec[1] = xrec[1] + 1;
        } else {
            qkRefCountMap.set(sk, [qk, 1]);
        }
    }, [qkRefCountMap]);

    const unregister = useCallback((qk: QueryKey) => {
        const sk = hashKey(qk);
        const xrec = qkRefCountMap.get(sk);
        if (xrec) {
            if (xrec[1] < 2) {
                qkRefCountMap.delete(sk);
            } else {
                xrec[1] = xrec[1] - 1;
            }
        }
    }, [qkRefCountMap]);

    const invalidate = useCallback(() => {
        const ps$: Promise<unknown>[] = [];
        qkRefCountMap.values().forEach(rec => {
            ps$.push(tqc.invalidateQueries({queryKey: rec[0]}));
        });
        return Promise.all(ps$)
            .then(() => {
                qkRefCountMap.clear();
            })
    }, [qkRefCountMap, tqc]);

    const context = useMemo(() => {
        const ctx: RefreshSet = {register, unregister, invalidate};
        return ctx;
    }, [invalidate, register, unregister]);

    return <RefreshContext value={context}>
        {children}
    </RefreshContext>;
}

export function useRefreshSet() {
    const rv = useContext(RefreshContext);
    return rv;
}

export function useRefreshableQuery<
    TQueryFnData,
    TError,
    TData,
    TQueryKey extends QueryKey,
>(options: UseQueryOptions<TQueryFnData, TError, TData, TQueryKey>) {
    const refreshManager = useRefreshSet();
    useEffect(() => {
        refreshManager.register(options.queryKey);
        return () => {
            refreshManager.unregister(options.queryKey);
        }
    }, [options.queryKey, refreshManager]);

    return useQuery(options);
}

export function useRefreshableInfallibleQuery<
    TQueryFnData,
    TError,
    TData,
    TQueryKey extends QueryKey,
>(options: UseQueryOptions<TQueryFnData, TError, TData, TQueryKey>): InfallibleUseQueryResult<TData, TError> {
    const refreshManager = useRefreshSet();
    useEffect(() => {
        refreshManager.register(options.queryKey);
        return () => {
            refreshManager.unregister(options.queryKey);
        }
    }, [options.queryKey, refreshManager]);

    return useInfallibleQuery(options);
}

// the types needed to make a proper useRefreshableQueries are not exported and
// are very complex
export function useRefreshingForQueries(qos: { queryKey: QueryKey }[]) {
    const refreshManager = useRefreshSet();
    useEffect(() => {
        qos.forEach(qo => {
            refreshManager.register(qo.queryKey);
        });
        return () => {
            qos.forEach(qo => {
                refreshManager.unregister(qo.queryKey);
            })
        };
    }, [qos, refreshManager]);
}

const RefreshContext = createContext<RefreshSet>({
    register: () => {/**/
    },
    unregister: () => {/**/
    },
    invalidate: () => Promise.resolve(),
});